import { cn } from "@/lib/utils"
import { Slot } from "@radix-ui/react-slot"
import { LucideIcon } from "lucide-react"
import React, {
  createElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
import { StyledDiv } from "./layout"
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "./sheet"
import { Button } from "./button"
import * as PageRendering from "@/lib/page-rendering"

type SerialisablePrimitive = null | undefined | string | number | boolean
type SheetPageProps = React.PropsWithChildren<{
  href: string
  data?: Record<string, SerialisablePrimitive>
  dialogId?: string | null
}>

function SheetLoader({
  href,
  data,
}: {
  href: string
  data?: Record<string, SerialisablePrimitive>
}) {
  const navigate = PageRendering.useNavigate()
  useEffect(() => {
    navigate({ href, data })
  }, [href, data, navigate])

  return null
}

// Whether the visit that landed on this page asked for the sheet named
// `dialogId` to open at once.
function useOpensOnArrival(dialogId: string | null) {
  const {
    flash: { dialog_id: flashDialogId },
  } = PageRendering.useVisit()
  return dialogId !== null && dialogId === flashDialogId
}

// A page driven inside a sheet. With a `scope`, the sheet renders only the
// pages the predicate accepts and hands every other page to the page beneath
// it; without one it renders whatever it resolves, so an embedding that
// navigates several pages deep inside itself keeps doing so.
export function EmbeddedSheetPage({
  href,
  data,
  dialogId = null,
  scope,
  children,
}: SheetPageProps & { scope?: PageRendering.Scope }) {
  const [open, setOpen] = useState(useOpensOnArrival(dialogId))
  const fetchPage = PageRendering.buildInertiaFetchPage()
  const receive = PageRendering.useReceive()

  // The sheet retires itself before delegating. With state preserved, the
  // page beneath after the hand-off is the same component instance the sheet
  // is mounted in, which would otherwise keep the sheet's open state with it.
  // The enclosing host takes the page: an enclosing driver when there is
  // one, the root otherwise.
  const host = useCallback(
    (page: PageRendering.Page) => {
      setOpen(false)
      receive(page)
    },
    [receive],
  )

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      {children}
      <SheetContent className="p-0" aria-describedby={undefined}>
        <SheetTitle className="sr-only" />
        <PageRendering.Driver
          initialPage={{
            name: "sheet-page/loading",
            baseUrl: new URL(href, window.location.href),
            node: <SheetLoader href={href} data={data} />,
            props: {},
            transient: {},
          }}
          fetchPage={fetchPage}
          scope={scope}
          host={host}
        />
      </SheetContent>
    </Sheet>
  )
}

export function EmbeddedSheetPageClose({
  asChild,
  ...props
}: { asChild?: boolean } & React.ComponentProps<typeof Button>) {
  const Comp = asChild ? Slot : Button

  return (
    <SheetClose asChild>
      <Comp {...props} />
    </SheetClose>
  )
}

export function InertiaSheetPage({
  href,
  data = {},
  dialogId = null,
  children,
}: SheetPageProps) {
  const iframeSrc = useMemo(() => {
    const url = new URL(href, window.location.origin)

    for (const [key, value] of Object.entries(data)) {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, String(value))
      }
    }

    url.searchParams.set("sheet", "1")

    return url.toString()
  }, [href, data])

  const [reloadOnClose, setReloadOnClose] = useState(false)
  const [open, setOpen] = useState(useOpensOnArrival(dialogId))
  const navigate = PageRendering.useNavigate()
  const { url } = PageRendering.useEntry()

  useEffect(() => {
    window.addEventListener("message", (ev) => {
      if (ev.data === "sheet:close") {
        setOpen(false)
      }

      if (ev.data === "sheet:reload_on_close") {
        setReloadOnClose(true)
      }
    })
  }, [])

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      {children}
      <SheetContent
        className="p-0"
        onCloseAutoFocus={() =>
          reloadOnClose &&
          navigate({ href: url, replace: true, preserveScroll: true })
        }
        aria-describedby={undefined}
      >
        <SheetTitle className="sr-only" />
        <iframe
          id="sheet-iframe"
          src={iframeSrc}
          className={cn("absolute top-0 bottom-0 h-full w-full")}
        />
      </SheetContent>
    </Sheet>
  )
}

export const SheetPageTrigger = SheetTrigger

export const SheetPageBody = StyledDiv("p-6 mt-4")

export function SheetPageTitle({
  className,
  icon,
  children,
  ref,
  id,
  ...props
}: React.ComponentPropsWithRef<"div"> & { icon: LucideIcon }) {
  return (
    <div
      ref={ref}
      className={cn(
        "inline-flex items-center gap-1 bg-transparent text-2xl leading-none font-semibold tracking-tight text-black",
        className,
      )}
      role="heading"
      aria-level={1}
      {...props}
    >
      {createElement(icon, { size: 24 })}
      <span id={id}>{children}</span>
    </div>
  )
}

export function InertiaSheetPageClose({
  asChild,
  ...props
}: { asChild?: boolean } & React.ComponentProps<typeof Button>) {
  const Comp = asChild ? Slot : Button
  return (
    <Comp {...props} onClick={() => window.parent.postMessage("sheet:close")} />
  )
}

export {
  InertiaSheetPage as SheetPage,
  InertiaSheetPageClose as SheetPageClose,
}
